import { INVENTORY_CONTEXT } from 'constants/inventory';

const CONTEXT_LABEL_MAP = {};
const CONTEXT_ENUM_ORDER = {};
INVENTORY_CONTEXT.forEach(({ value, label }, index) => {
  CONTEXT_LABEL_MAP[value] = label;
  CONTEXT_ENUM_ORDER[value] = index;
});

export const getLedgerContextLabel = context => {
  if (!context) return '';
  return (
    CONTEXT_LABEL_MAP[context] ??
    context
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};

// Known contexts keep INVENTORY_CONTEXT enum order; unknown ones follow alphabetically
const sortContexts = contexts =>
  [...contexts].sort((a, b) => {
    const orderA = CONTEXT_ENUM_ORDER[a];
    const orderB = CONTEXT_ENUM_ORDER[b];
    if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
    if (orderA !== undefined) return -1;
    if (orderB !== undefined) return 1;
    return a.localeCompare(b);
  });

const toNumber = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const roundTo1 = value => Math.round(value * 10) / 10;

const daysInRange = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const diff = Math.round((end - start) / (1000 * 60 * 60 * 24));
  return Math.max(diff + 1, 1);
};

// CSV rows: { date, sku, product_name, brand_name, context, quantity_in, quantity_out }
const groupRows = rows => {
  const skuMap = new Map();
  const dateSet = new Set();
  const contextSet = new Set();

  rows.forEach(row => {
    const { date, sku, context } = row;
    if (!date || !sku) return;

    const quantityIn = toNumber(row.quantity_in);
    const quantityOut = toNumber(row.quantity_out);

    dateSet.add(date);
    contextSet.add(context);

    if (!skuMap.has(sku)) {
      skuMap.set(sku, {
        sku,
        productName: row.product_name,
        brandName: row.brand_name,
        totalIn: 0,
        totalOut: 0,
        inByDate: new Map(),
        outByDate: new Map(),
        netByDateContext: new Map(),
      });
    }

    const entry = skuMap.get(sku);
    entry.totalIn += quantityIn;
    entry.totalOut += quantityOut;
    entry.inByDate.set(date, (entry.inByDate.get(date) ?? 0) + quantityIn);
    entry.outByDate.set(date, (entry.outByDate.get(date) ?? 0) + quantityOut);

    const key = `${date}|${context}`;
    entry.netByDateContext.set(
      key,
      (entry.netByDateContext.get(key) ?? 0) + quantityIn - quantityOut,
    );
  });

  return {
    skus: [...skuMap.values()].sort((a, b) => a.sku.localeCompare(b.sku)),
    dates: [...dateSet].sort(),
    // the same context set is repeated under every date so columns stay uniform
    contexts: sortContexts(contextSet),
  };
};

const FIXED_HEADERS = [
  'SKU',
  'Product Name',
  'Brand',
  'Total In',
  'Total Out',
  'Daily Avg In',
  'Daily Avg Out',
];
const FIXED_COL_COUNT = FIXED_HEADERS.length;

const HEADER_STYLE = {
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6E4F0' } },
  font: { bold: true },
  alignment: { horizontal: 'center', vertical: 'middle' },
};

// Two header rows: fixed columns merged vertically, each date merged over
// its sub-columns on row 1 with the sub-labels on row 2.
const writeHeader = (sheet, dateGroups) => {
  const row1 = sheet.getRow(1);
  const row2 = sheet.getRow(2);

  FIXED_HEADERS.forEach((label, index) => {
    row1.getCell(index + 1).value = label;
    sheet.mergeCells(1, index + 1, 2, index + 1);
  });

  let col = FIXED_COL_COUNT + 1;
  dateGroups.forEach(({ date, subLabels }) => {
    row1.getCell(col).value = date;
    if (subLabels.length > 1) {
      sheet.mergeCells(1, col, 1, col + subLabels.length - 1);
    }
    subLabels.forEach((label, index) => {
      row2.getCell(col + index).value = label;
    });
    col += subLabels.length;
  });

  const totalCols = col - 1;
  [row1, row2].forEach(row => {
    for (let c = 1; c <= totalCols; c += 1) {
      const cell = row.getCell(c);
      cell.fill = HEADER_STYLE.fill;
      cell.font = HEADER_STYLE.font;
      cell.alignment = HEADER_STYLE.alignment;
    }
  });

  sheet.views = [{ state: 'frozen', xSplit: FIXED_COL_COUNT, ySplit: 2 }];

  const widths = [18, 40, 18, 10, 10, 12, 12];
  widths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });
};

const fixedCells = (entry, numDays) => [
  entry.sku,
  entry.productName,
  entry.brandName,
  entry.totalIn,
  entry.totalOut,
  roundTo1(entry.totalIn / numDays),
  roundTo1(entry.totalOut / numDays),
];

const AVG_NUM_FMT = '0.0';

// Summary: one In / Out column pair per date; cells with no movement stay blank
const buildSummarySheet = (sheet, { skus, dates }, numDays) => {
  writeHeader(
    sheet,
    dates.map(date => ({ date, subLabels: ['In', 'Out'] })),
  );

  skus.forEach(entry => {
    sheet.addRow([
      ...fixedCells(entry, numDays),
      ...dates.flatMap(date => [
        entry.inByDate.get(date) || null,
        entry.outByDate.get(date) || null,
      ]),
    ]);
  });

  sheet.getColumn(6).numFmt = AVG_NUM_FMT;
  sheet.getColumn(7).numFmt = AVG_NUM_FMT;
  dates.forEach((date, index) => {
    sheet.getColumn(FIXED_COL_COUNT + index * 2 + 1).width = 10;
    sheet.getColumn(FIXED_COL_COUNT + index * 2 + 2).width = 10;
  });
};

// Detail: one column per date x context with signed net movement
const buildDetailSheet = (sheet, { skus, dates, contexts }, numDays) => {
  const subLabels = contexts.map(getLedgerContextLabel);
  writeHeader(
    sheet,
    dates.map(date => ({ date, subLabels })),
  );

  skus.forEach(entry => {
    sheet.addRow([
      ...fixedCells(entry, numDays),
      ...dates.flatMap(date =>
        contexts.map(
          context => entry.netByDateContext.get(`${date}|${context}`) ?? null,
        ),
      ),
    ]);
  });

  sheet.getColumn(6).numFmt = AVG_NUM_FMT;
  sheet.getColumn(7).numFmt = AVG_NUM_FMT;
  dates.forEach((date, dateIndex) => {
    contexts.forEach((context, contextIndex) => {
      sheet.getColumn(
        FIXED_COL_COUNT + dateIndex * contexts.length + contextIndex + 1,
      ).width = 16;
    });
  });
};

const buildStockLedgerWorkbook = async (rows, { startDate, endDate }) => {
  const { default: ExcelJS } = await import('exceljs');

  const grouped = groupRows(rows);
  const numDays = daysInRange(startDate, endDate);

  const workbook = new ExcelJS.Workbook();
  buildSummarySheet(workbook.addWorksheet('Summary'), grouped, numDays);
  buildDetailSheet(workbook.addWorksheet('Detail'), grouped, numDays);
  return workbook;
};

export default buildStockLedgerWorkbook;
